import { useMemo } from "react";
import { getShotMapCalibration } from "../utils/shotMapCalibration";

const outcomeValue = (value) => {
  if (value === "Goal") return 3;
  if (value === "2 Point") return 2;
  if (value === "Point") return 1;
  return 0;
};

const outcomeStroke = (shotOutcomeValue) => {
  if (shotOutcomeValue === "Point") return "#FFFFFF";
  if (shotOutcomeValue === "2 Point") return "#1f77b4";
  if (shotOutcomeValue === "Goal") return "#006400";
  return "#FF0000";
};

const {
  INNER_LEFT_PX,
  INNER_TOP_PX,
  INNER_RIGHT_PX,
  INNER_BOTTOM_PX,
  X_PCT_OFFSET,
  X_PCT_SCALE,
  Y_PCT_OFFSET,
  Y_PCT_SCALE,
} = getShotMapCalibration();

const INNER_WIDTH_PX = INNER_RIGHT_PX - INNER_LEFT_PX;
const INNER_HEIGHT_PX = INNER_BOTTOM_PX - INNER_TOP_PX;

export const useShotMarkers = (shots = []) => {
  const markers = useMemo(() => {
    if (!shots.length) return [];

  return shots.map((shot, idx) => {
      const expectedValue = Number(shot.expectedScore || 0);
      const roundedValue = Math.round(expectedValue * 10) / 10;
      let labelText = roundedValue.toFixed(1).replace(/^0/, "");
      if (labelText.endsWith(".0")) {
        labelText = labelText.replace(".0", "");
      }
      if (labelText === "") {
        labelText = "0";
      }

      const angleRad = (Number(shot.angle || 0) * Math.PI) / 180;
      const distance = Number(shot.distance || 0);
      const sideMult = shot.side === "Left" ? -1 : 1;

      const dx = distance * Math.sin(angleRad) * sideMult;
      const dy = distance * Math.cos(angleRad);
      const xPitchPct = X_PCT_OFFSET + X_PCT_SCALE * dx;
      const yPitchPct = Y_PCT_OFFSET + Y_PCT_SCALE * dy;

      const xSvg = INNER_LEFT_PX + xPitchPct * INNER_WIDTH_PX;
      const ySvg = INNER_TOP_PX + yPitchPct * INNER_HEIGHT_PX;

      let shapeClass;
      if (shot.shotType === "Point attempt") {
        shapeClass = shot.setPlay === "Open Play" ? "circle" : "square";
      } else if (shot.shotType === "2 Point attempt") {
        shapeClass = "diamond";
      } else {
        shapeClass = "triangle";
      }

      return {
        id: idx,
        shape: shapeClass,
        stroke: outcomeStroke(shot.shotOutcome),
        x: xSvg,
        y: ySvg,
        tooltipText: `xP:${Number(shot.expectedScore || 0).toFixed(2)}-${
          shot.shotPressure || "Unknown"
        }-${shot.playerName || "Unknown"}`,
        labelText,
      };
    });
  }, [shots]);

  const summary = useMemo(() => {
    const total = shots.length;
    const scored = shots.reduce(
      (sum, shot) => sum + (outcomeValue(shot.shotOutcome) > 0 ? 1 : 0),
      0,
    );
    const pts = shots.reduce(
      (sum, shot) => sum + outcomeValue(shot.shotOutcome),
      0,
    );
    const xp = shots.reduce(
      (sum, shot) => sum + Number(shot.expectedScore || 0),
      0,
    );
    return {
      total,
      scored,
      conversionRate: total > 0 ? (scored / total).toFixed(2) : "",
      pts,
      xp,
      eff: xp > 0 ? (pts / xp).toFixed(2) : "-",
    };
  }, [shots]);

  return { markers, summary };
};
